/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $wrapNodeInElement, mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $createRangeSelection,
  $getSelection,
  $insertNodes,
  $isNodeSelection,
  $isRootOrShadowRoot,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  PASTE_COMMAND,
} from "lexical";
import { useEffect, useRef, useState } from "react";
import * as React from "react";
import { useApolloClient } from "@apollo/client";

import { Box, Button, Grid, TextField } from "@mui/material";

import { CAN_USE_DOM } from "../../../common/utils/canUseDom";
import {
  $createImageNode,
  $isImageNode,
  ImageNode,
} from "../../CustomNodes/ImageNode";
import { isBlockedFile } from "../../../../files/utils";
import { uploadImageFile } from "../../../utils/uploadImage";
import { useCurrentUser } from "../../../../../providers/UserProvider";
import { useOnlineStatus } from "../../../../../hooks/offline-hooks/offline-misc";
import { showToast } from "../../../../../graphql/cache/modal";
import { toastVariant } from "../../../../../constants/misc";

const getDOMSelection = (targetWindow) =>
  CAN_USE_DOM ? (targetWindow || window).getSelection() : null;

export const INSERT_IMAGE_COMMAND = createCommand("INSERT_IMAGE_COMMAND");

export function InsertImageUriDialogBody({ onClick }) {
  const [src, setSrc] = useState("");
  const [altText, setAltText] = useState("");

  const isDisabled = src === "";

  return (
    <>
      <TextField
        label="Image URL"
        placeholder="i.e. https://source.unsplash.com/random"
        onChange={(e) => setSrc(e.target.value)}
        value={src}
        sx={{ mb: 7, height: 10 }}
        fullWidth
      />
      <TextField
        label="Alt Text"
        placeholder="Random unsplash image"
        onChange={(e) => setAltText(e.target.value)}
        sx={{ mb: 7, height: 10 }}
        fullWidth
        value={altText}
        data-test-id="image-modal-alt-text-input"
      />
      <Grid container justifyContent="flex-end">
        <Button
          data-test-id="image-modal-confirm-btn"
          disabled={isDisabled}
          onClick={() => onClick({ altText, src })}
          variant="outlined"
        >
          Confirm
        </Button>
      </Grid>
    </>
  );
}

export function InsertImageUploadedDialogBody({ onClick }) {
  const [src, setSrc] = useState("");
  const [altText, setAltText] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const apolloClient = useApolloClient();
  const { user: currentUser } = useCurrentUser();

  const isDisabled = src === "" || isUploading;

  const loadImage = (files) => {
    const rejected = files.filter(isBlockedFile);

    if (rejected.length) {
      alert(
        `These file types are not allowed: ${rejected
          .map((f) => f.name)
          .join(', ')}`,
      );
      return;
    }
    if (files !== null && files[0]) {
      setIsUploading(true);
      uploadImageFile(files[0], { apolloClient, currentUser })
        .then((uploadedSrc) => setSrc(uploadedSrc))
        .catch(() =>
          showToast({
            variant: toastVariant.danger,
            title: "Image upload failed",
          })
        )
        .finally(() => setIsUploading(false));
    }
  };

  return (
    <>
      <Button fullWidth sx={{ mb: 1 }} variant="contained" component="label">
        {isUploading ? "Uploading..." : "Upload"}
        <input
          onChange={(e) => loadImage(e.target.files)}
          hidden
          accept="image/*"
          multiple
          type="file"
          disabled={isUploading}
        />
      </Button>

      <TextField
        label="Alt Text"
        placeholder="Descriptive alternative text"
        onChange={(e) => setAltText(e.target.value)}
        value={altText}
        sx={{ mb: 7, height: 10 }}
        fullWidth
        variant="standard"
        data-test-id="image-modal-alt-text-input"
      />
      <Grid container justifyContent="flex-end">
        <Button
          data-test-id="image-modal-confirm-btn"
          disabled={isDisabled}
          onClick={() => onClick({ altText, src })}
          variant="outlined"
        >
          Confirm
        </Button>
      </Grid>
    </>
  );
}

export function InsertImageDialog({ activeEditor, onClose }) {
  const [mode, setMode] = useState(null);
  const hasModifier = useRef(false);

  useEffect(() => {
    hasModifier.current = false;
    const handler = (e) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener("keydown", handler);
    return () => {
      document.removeEventListener("keydown", handler);
    };
  }, [activeEditor]);

  const onClick = (payload) => {
    activeEditor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
    onClose();
  };

  return (
    <>
      {!mode && (
        <Box>
          <Button
            data-test-id="image-modal-option-url"
            onClick={() => setMode("url")}
          >
            URL
          </Button>
          <Button
            data-test-id="image-modal-option-file"
            onClick={() => setMode("file")}
          >
            File
          </Button>
        </Box>
      )}
      {mode === "url" && <InsertImageUriDialogBody onClick={onClick} />}
      {mode === "file" && <InsertImageUploadedDialogBody onClick={onClick} />}
    </>
  );
}

function getImageFilesFromDataTransfer(dataTransfer) {
  const items = dataTransfer?.items;
  if (!items) {
    return [];
  }
  const files = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind === "file" && item.type.startsWith("image/")) {
      const file = item.getAsFile();
      if (file) {
        files.push(file);
      }
    }
  }
  return files;
}

function hasDraggedImageFile(event) {
  const items = event.dataTransfer?.items;
  if (!items) {
    return false;
  }
  for (let i = 0; i < items.length; i++) {
    if (items[i].kind === "file" && items[i].type.startsWith("image/")) {
      return true;
    }
  }
  return false;
}

function uploadAndInsertImages(
  files,
  { editor, apolloClient, currentUser, isOnline }
) {
  if (!isOnline) {
    showToast({
      variant: toastVariant.warning,
      title: "Cannot add images while offline",
    });
    return;
  }

  files.forEach((file) => {
    uploadImageFile(file, { apolloClient, currentUser })
      .then((src) => {
        editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
          altText: file.name || "Pasted image",
          src,
        });
      })
      .catch(() =>
        showToast({
          variant: toastVariant.danger,
          title: "Image upload failed",
        })
      );
  });
}

export default function ImagesPlugin({ captionsEnabled }) {
  const [editor] = useLexicalComposerContext();
  const apolloClient = useApolloClient();
  const { user: currentUser } = useCurrentUser();
  const isOnline = useOnlineStatus();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode])) {
      throw new Error("ImagesPlugin: ImageNode not registered on editor");
    }

    return mergeRegister(
      editor.registerCommand(
        INSERT_IMAGE_COMMAND,
        (payload) => {
          const imageNode = $createImageNode(payload);
          $insertNodes([imageNode]);
          if ($isRootOrShadowRoot(imageNode.getParentOrThrow())) {
            $wrapNodeInElement(imageNode, $createParagraphNode).selectEnd();
          }

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          return onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        DRAGOVER_COMMAND,
        (event) => {
          if (getImageNodeInSelection()) {
            return onDragover(event, editor);
          }
          if (hasDraggedImageFile(event)) {
            event.preventDefault();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        DROP_COMMAND,
        (event) => {
          if (getImageNodeInSelection()) {
            return onDrop(event, editor);
          }
          if (!editor.isEditable()) {
            return false;
          }
          const files = getImageFilesFromDataTransfer(event.dataTransfer);
          if (files.length === 0) {
            return false;
          }
          event.preventDefault();
          if (!canDropImage(event, editor)) {
            return true;
          }

          const range = getDragSelection(event);
          const rangeSelection = $createRangeSelection();
          if (range !== null && range !== undefined) {
            rangeSelection.applyDOMRange(range);
          }
          $setSelection(rangeSelection);

          uploadAndInsertImages(files, {
            editor,
            apolloClient,
            currentUser,
            isOnline,
          });

          return true;
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        PASTE_COMMAND,
        (event) => {
          if (!editor.isEditable()) {
            return false;
          }
          const files = getImageFilesFromDataTransfer(event.clipboardData);
          if (files.length === 0) {
            return false;
          }
          event.preventDefault();

          uploadAndInsertImages(files, {
            editor,
            apolloClient,
            currentUser,
            isOnline,
          });

          return true;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [apolloClient, captionsEnabled, currentUser, editor, isOnline]);

  return null;
}

const TRANSPARENT_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
const img = document.createElement("img");
img.src = TRANSPARENT_IMAGE;

function onDragStart(event) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData("text/plain", "_");
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    "application/x-lexical-drag",
    JSON.stringify({
      data: {
        altText: node.__altText,
        caption: node.__caption,
        height: node.__height,
        key: node.getKey(),
        maxWidth: node.__maxWidth,
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width,
      },
      type: "image",
    })
  );

  return true;
}

function onDragover(event, editor) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropImage(event, editor)) {
    event.preventDefault();
  }
  return true;
}

function onDrop(event, editor) {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropImage(event, editor)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, data);
  }
  return true;
}

function getImageNodeInSelection() {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isImageNode(node) ? node : null;
}

function getDragImageData(event) {
  const dragData = event.dataTransfer?.getData("application/x-lexical-drag");
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== "image") {
    return null;
  }

  return data;
}

function canDropImage(event, editor) {
  const target = event.target;
  const rootElement = editor?.getRootElement();
  if (!rootElement || !target || !(target instanceof Node)) {
    return false;
  }
  if (!rootElement.contains(target)) {
    return false;
  }
  const targetElement = target instanceof HTMLElement ? target : target.parentElement;
  return !targetElement?.closest("code, span.editor-image");
}

function getDragSelection(event) {
  let range;
  const target = event.target;
  const targetWindow =
    target == null
      ? null
      : target.nodeType === 9
      ? target.defaultView
      : target.ownerDocument.defaultView;
  const domSelection = getDOMSelection(targetWindow);
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
    range = domSelection.getRangeAt(0);
  } else {
    throw Error(`Cannot get the selection when dragging`);
  }

  return range;
}
